import { gql, useQuery } from "@apollo/client";
import { type LocationGenerics } from "router/location";
import { closeModal, dispatchModal } from "utils";
import { Loader } from "components";
import moment from "moment";
import numeral from "numeral";
import { useSearch } from "react-location";
import { BillViewContainer } from "../cards/bill-view";

const GET_BILLS = gql`
  query GetServicePointBills(
    $servicePoint: ID
    $page: Int
    $pageSize: Int
    $sort: String
    $status: BillStatus
  ) {
    items: getBills(
      servicePoint: $servicePoint
      page: $page
      pageSize: $pageSize
      sort: $sort
      status: $status
    ) {
      _id
      code
      month
      year
      billConsumptionValue
      billAmount
      adjustmentAmount
      billDate
      downloadUrl
      createdAt
    }
  }
`;

interface BillsContainerProps {
  servicePoint: string;
}

export const BillsContainer = ({ servicePoint }: BillsContainerProps) => {
  const searchParams = useSearch<LocationGenerics>();
  const { loading, data, refetch } = useQuery(GET_BILLS, {
    notifyOnNetworkStatusChange: false,
    variables: {
      servicePoint,
      sort: "-createdAt",
      page: 0,
      pageSize: 0,
      status: "Processed",
    },
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return data?.items?.length > 0 ? (
    <div className="flex-1 p-6 flow-root overflow-y-auto">
      <ul className="space-y-4">
        {data?.items?.map((historyItem: any) => (
          <HistoryDetails key={historyItem._id} history={historyItem} />
        ))}
      </ul>

      {Boolean(searchParams.id?.length) && (
        <>
          <BillViewContainer
            open={searchParams.modal === "view"}
            setOpen={closeModal}
            refetch={() => {
              void refetch();
            }}
          />
        </>
      )}
    </div>
  ) : (
    <div className="flex min-h-[65vh] items-center justify-center">
      <div className="text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          No Bills Yet
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Kindly Refer To CMS For Billing History.
        </p>
      </div>
    </div>
  );
};

const HistoryDetails = ({ history }: { history: any }) => {
  return (
    <>
      <button
        onClick={dispatchModal(history._id, "view")}
        className="relative border border-dashed grid-cols-4 border-gray-300 rounded cursor-pointer hover:bg-gray-100 w-full text-left"
      >
        <div className="grid grid-cols-4 gap-4  p-4">
          <div>
            <span className="block text-xs font-light">Billing Cycle</span>
            <div className="mt-1 block w-full sm:text-sm">
              {moment({
                year: history.year,
                month: history.month - 1,
              }).format("MMMM, YYYY")}
            </div>
          </div>
          <div>
            <span className="block text-xs font-light">Bill Date</span>
            <div className="mt-1 block w-full sm:text-sm">
              {history?.billDate
                ? moment(history.billDate).format("DD/MM/YYYY")
                : "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-xs font-light">Consumption Billed</span>
            <div className="mt-1 block w-full sm:text-sm">
              {numeral(history.billConsumptionValue || 0).format("0,0")} KWh
            </div>
          </div>
          <div>
            <span className="block text-xs font-light">Bill Amount</span>
            <div className="mt-1 block w-full sm:text-sm">
              GHS{" "}
              {numeral(history.billAmount + history.adjustmentAmount).format(
                "0,0.00",
              )}
            </div>
          </div>
        </div>
      </button>
    </>
  );
};
